import { useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';

/**
 * InteractiveImageAccordion — horizontal hover-to-expand image stripe.
 *
 * Each item collapses to a thin vertical bar (60px) with a 90°-rotated
 * caption; the hovered/focused item expands to ~400px and shows the
 * caption horizontally at the bottom.
 *
 * Adapted from a public component; changes vs. the source:
 *   - proper TypeScript types (the source had implicit `any` props)
 *   - modern Tailwind opacity syntax (`bg-black/40` instead of deprecated
 *     `bg-opacity-40` modifiers)
 *   - mobile/touch support via onClick (hover doesn't fire on touch)
 *   - keyboard navigation (Enter / Space activates, focus visible)
 *   - rendered as a flex row that fills its container (no horizontal
 *     scroll — better for the hero where width is bounded)
 */

export interface AccordionItem {
  id: string | number;
  title: string;
  imageUrl: string;
  /** Optional second line below the title, only shown when expanded. */
  subtitle?: string;
}

interface InteractiveImageAccordionProps {
  items: AccordionItem[];
  /** Initial expanded index. Defaults to the last item (matches the source design). */
  initialActiveIndex?: number;
  /** Optional class on the outer container. */
  className?: string;
  /** Optional fixed height — defaults to a sensible aspect for hero placement. */
  heightClassName?: string;
}

export function InteractiveImageAccordion({
  items,
  initialActiveIndex,
  className,
  heightClassName = 'h-[420px] md:h-[480px]',
}: InteractiveImageAccordionProps) {
  const [activeIndex, setActiveIndex] = useState<number>(
    typeof initialActiveIndex === 'number'
      ? Math.min(initialActiveIndex, items.length - 1)
      : items.length - 1,
  );

  return (
    <div className={cn('flex flex-row items-center gap-2 md:gap-3 w-full', className)}>
      {items.map((item, index) => (
        <AccordionPanel
          key={item.id}
          item={item}
          isActive={index === activeIndex}
          heightClassName={heightClassName}
          onActivate={() => setActiveIndex(index)}
        />
      ))}
    </div>
  );
}

interface AccordionPanelProps {
  item: AccordionItem;
  isActive: boolean;
  heightClassName: string;
  onActivate: () => void;
}

function AccordionPanel({ item, isActive, heightClassName, onActivate }: AccordionPanelProps) {
  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      aria-label={item.title}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onClick={onActivate}
      onKeyDown={handleKey}
      className={cn(
        'relative rounded-2xl overflow-hidden cursor-pointer outline-none',
        'transition-[flex-grow,flex-basis] duration-700 ease-in-out',
        'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        heightClassName,
        // The active panel grows to take the lion's share; inactive panels stay
        // narrow. flex-basis kept tiny so the active panel can stretch.
        isActive ? 'flex-[6_1_0%] min-w-[140px]' : 'flex-[1_1_0%] min-w-[56px]',
      )}
    >
      <img
        src={item.imageUrl}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => {
          const t = e.currentTarget;
          // Avoid loop if the placeholder also fails
          t.onerror = null;
          t.src = 'https://placehold.co/600x600/2d3748/ffffff?text=Image+Error';
        }}
      />
      {/* Dark overlay for legibility — slightly darker on inactive so the
          rotated label reads cleanly on every image. */}
      <div
        className={cn(
          'absolute inset-0 transition-colors duration-500',
          isActive ? 'bg-black/35' : 'bg-black/55',
        )}
        aria-hidden="true"
      />

      {/* Caption */}
      <span
        className={cn(
          'absolute text-white font-semibold whitespace-nowrap',
          'transition-all duration-300 ease-in-out',
          isActive
            ? 'bottom-6 left-1/2 -translate-x-1/2 rotate-0 text-lg md:text-xl drop-shadow'
            : 'bottom-24 left-1/2 -translate-x-1/2 rotate-90 text-base',
        )}
      >
        {item.title}
      </span>

      {/* Subtitle (only shown when active, and only if provided) */}
      {item.subtitle && isActive && (
        <span
          className={cn(
            'absolute bottom-14 left-1/2 -translate-x-1/2 whitespace-nowrap',
            'text-xs md:text-sm text-slate-200/90',
            'transition-opacity duration-500 delay-200',
          )}
        >
          {item.subtitle}
        </span>
      )}
    </div>
  );
}
